import { useState } from 'react'
import { toast } from 'sonner'
import { useGenerateResetLink, useSetUserPassword } from '../../../hooks/admin/useUsers'
import { PasswordStrengthIndicator } from '../../PasswordStrengthIndicator'

interface SecurityTabProps {
  userId: number
}

/**
 * Credentials for one user.
 *
 * Everything here takes effect the moment it is clicked -- there is no Save
 * button and nothing to leave unsaved, which is exactly why it belongs away
 * from the profile form rather than sharing a page with one.
 */
export function SecurityTab({ userId }: SecurityTabProps) {
  const generateResetLink = useGenerateResetLink()
  const setUserPassword = useSetUserPassword()

  const [resetLink, setResetLink] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)
  const [chosenPassword, setChosenPassword] = useState('')
  const [showChosenPassword, setShowChosenPassword] = useState(false)
  const [issuedPassword, setIssuedPassword] = useState<string | null>(null)
  const [passwordCopied, setPasswordCopied] = useState(false)

  // Mirrors the server's policy in utils/password-validator: 8+ characters
  // with a lowercase letter and a digit. Kept here only to disable the button
  // early -- the server is what actually enforces it.
  const chosenPasswordValid =
    chosenPassword.length >= 8 &&
    /[a-z]/.test(chosenPassword) &&
    /\d/.test(chosenPassword)

  const handleSetPassword = async () => {
    if (!chosenPasswordValid) return
    await setUserPassword.mutateAsync({ id: userId, password: chosenPassword })
    // Nothing to reveal: the admin already has the value they typed.
    setChosenPassword('')
    setShowChosenPassword(false)
    setIssuedPassword(null)
    toast.success('Password set. The user can sign in with it now.')
  }

  const handleGeneratePassword = async () => {
    setPasswordCopied(false)
    const response = await setUserPassword.mutateAsync({ id: userId })
    setIssuedPassword(response.data?.password ?? null)
  }

  const handleCopyIssuedPassword = async () => {
    if (!issuedPassword) return
    try {
      await navigator.clipboard.writeText(issuedPassword)
      setPasswordCopied(true)
    } catch {
      // Clipboard can be blocked; the password stays selectable on screen.
      setPasswordCopied(false)
    }
  }

  const handleGenerateResetLink = async () => {
    setLinkCopied(false)
    const response = await generateResetLink.mutateAsync(userId)
    setResetLink(response.data?.link ?? null)
  }

  const handleCopyResetLink = async () => {
    if (!resetLink) return
    try {
      await navigator.clipboard.writeText(resetLink)
      setLinkCopied(true)
    } catch {
      // Clipboard can be blocked; the link stays selectable on screen.
      setLinkCopied(false)
    }
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold mb-1">Password</h2>
      <p className="text-gray-600 text-sm mb-4 max-w-2xl">
        Set this user's password directly. It takes effect immediately, replaces whatever password they had, and is
        permanent &mdash; they are never asked to change it. Tell them yourself; nothing here is emailed.
      </p>

      <div className="max-w-md">
        <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
          New password
        </label>
        <div className="flex gap-2">
          <input
            id="new-password"
            type={showChosenPassword ? 'text' : 'password'}
            value={chosenPassword}
            onChange={(e) => setChosenPassword(e.target.value)}
            autoComplete="new-password"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="At least 8 characters"
          />
          <button
            type="button"
            onClick={() => setShowChosenPassword((v) => !v)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {showChosenPassword ? 'Hide' : 'Show'}
          </button>
        </div>
        {chosenPassword && <PasswordStrengthIndicator password={chosenPassword} className="mt-3" />}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          type="button"
          onClick={handleSetPassword}
          disabled={!chosenPasswordValid || setUserPassword.isPending}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {setUserPassword.isPending ? 'Working…' : 'Set password'}
        </button>
        <button
          type="button"
          onClick={handleGeneratePassword}
          disabled={setUserPassword.isPending}
          className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Generate a password for me
        </button>
      </div>

      {issuedPassword && (
        <div className="mt-4 p-3 border border-gray-200 rounded bg-gray-50 max-w-2xl">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-sm font-medium text-gray-700">New password &mdash; now in effect</span>
            <button
              type="button"
              onClick={handleCopyIssuedPassword}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
            >
              {passwordCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <code className="block text-sm break-all text-gray-800">{issuedPassword}</code>
          <p className="mt-2 text-xs text-gray-500">
            This is now the user's password and it will not expire or prompt them to change it. Shown once and not
            stored, and their old password no longer works &mdash; pass it on before leaving the page, or generate
            another if you lose it.
          </p>
        </div>
      )}

      <hr className="my-6 border-gray-200" />

      <h2 className="text-xl font-semibold mb-1">Reset link</h2>
      <p className="text-gray-600 text-sm mb-4 max-w-2xl">
        If this user can actually read the address on their account, generate a one-time link that lets them choose
        their own password. Send it to them yourself &mdash; it is not emailed. Anyone holding the link can set the
        password.
      </p>
      <button
        type="button"
        onClick={handleGenerateResetLink}
        disabled={generateResetLink.isPending}
        className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {generateResetLink.isPending ? 'Generating…' : 'Generate reset link'}
      </button>

      {resetLink && (
        <div className="mt-4 p-3 border border-gray-200 rounded bg-gray-50 max-w-2xl">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-sm font-medium text-gray-700">One-time reset link</span>
            <button
              type="button"
              onClick={handleCopyResetLink}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
            >
              {linkCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <code className="block text-xs break-all text-gray-800">{resetLink}</code>
          <p className="mt-2 text-xs text-gray-500">Shown once and not stored. Generate a new one if you lose it.</p>
        </div>
      )}
    </div>
  )
}
